import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import classNames from 'classnames'
import { Icon } from '@lightspeed/design-system-react'
import { vn, isFinite } from 'vend-number'
import { useT } from '@transifex/react'
import { displayRound } from '../../../filters/display-round'
import { DiscountValidationParams } from '../../../sale-calculation/calculate-sale'

type Props = {
  discountIsPercentage: boolean
  maxSaleDiscount: string | number
  displayDiscount: string | number
  saleIsReturn: boolean
  currencySymbol: string
  onValidChange?: (isValid: boolean) => void // callback when the input validity changed
  containerCss?: string // the css class name so that the container can position the input where it want to
  onEnter?: () => void // callback when enter key hit on the input
}
/**
 * Type of the forwarded ref to the component
 * Allow the forwared ref to select text & get the value in the input
 */
export type DiscountInputRef = {
  select: () => void
  getValue: () => string
}

/**
 * Sale discount input component with currency symbol
 */
export const SaleDiscountInput = forwardRef<DiscountInputRef, Props>(
  (props: Props, forwardedRef) => {
    const {
      discountIsPercentage,
      maxSaleDiscount,
      displayDiscount,
      saleIsReturn,
      currencySymbol,
      onValidChange,
      containerCss,
      onEnter,
    } = props

    const t = useT()

    const validateSaleDiscount = useCallback(
      ({
        displayDiscount,
        maxDisplayDiscount,
        saleIsReturn,
        discountSymbol,
      }: DiscountValidationParams & { discountSymbol: string }) => {
        /**
         * empty input is valid
         * saleIsReturn is valid as explained in isSaleDiscountValid in calculate-sale.ts
         */
        if (!displayDiscount || saleIsReturn) {
          return { isValid: true, errMsg: '' }
        }

        const context = 'Sale discount invalid error'
        if (!isFinite(displayDiscount)) {
          return {
            isValid: false,
            errMsg: t('Please use numbers only.', {
              _context: context,
            }),
          }
        }

        const discount = vn(displayDiscount)

        if (discount.lt(0)) {
          return {
            isValid: false,
            errMsg: t('Discount amount cannot be a negative value.', {
              _context: context,
            }),
          }
        }

        if (discount.gt(maxDisplayDiscount)) {
          return {
            isValid: false,
            errMsg: t('Maximum of {maxDisplayDiscount}{discountSymbol}', {
              _context: context,
              maxDisplayDiscount,
              discountSymbol,
            }),
          }
        }

        return { isValid: true, errMsg: '' }
      },
      [t]
    )
    const formatDiscount = useCallback(
      (number: number | string, isPercentage: boolean) => {
        const decimalPlaces = 2
        const trimInsignificantDigits = isPercentage
        return displayRound(number, decimalPlaces, trimInsignificantDigits)
      },
      []
    )
    const discountSymbol = discountIsPercentage ? '%' : currencySymbol
    const inputCssClass = discountIsPercentage
      ? 'vd-input--icon-right'
      : 'vd-input--icon-left'
    const symbolCssClass = discountIsPercentage
      ? 'vd-input-icon--right'
      : 'vd-input-icon--left'

    const [isValid, setIsValid] = useState(true)
    const [errorMsg, setErrorMsg] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      setInputRefValue(String(displayDiscount))
    }, [displayDiscount])

    /**
     * re-validate and format the input if needed
     */
    const reValidateAndFormat = useCallback(
      (value: string, shouldFormat = false) => {
        const { isValid, errMsg } = validateSaleDiscount({
          displayDiscount: value,
          maxDisplayDiscount: maxSaleDiscount,
          saleIsReturn,
          discountSymbol,
        })

        // only format when valid
        const inputValue =
          shouldFormat && isValid
            ? formatDiscount(value, discountIsPercentage)
            : value

        setIsValid(isValid)
        setErrorMsg(errMsg)
        setInputRefValue(String(inputValue))
      },
      [
        discountSymbol,
        maxSaleDiscount,
        saleIsReturn,
        discountIsPercentage,
        formatDiscount,
        validateSaleDiscount,
      ]
    )

    /**
     * Effect run when props changes, like discountIsPercentage change
     * Need to revalidate, reformat the input value
     */
    useEffect(() => {
      if (!inputRef.current) {
        return
      }

      const value = inputRef.current.value
      reValidateAndFormat(value, true)
    }, [reValidateAndFormat])

    /**
     * When user type in the discount, validate the input, no need to format
     */
    const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      reValidateAndFormat(value, false)
    }

    const formatInputWhenValid = () => {
      if (inputRef.current && isValid) {
        const formattedDiscount = formatDiscount(
          inputRef.current.value,
          discountIsPercentage
        )
        setInputRefValue(String(formattedDiscount))
      }
    }

    const setInputRefValue = (value: string) => {
      if (inputRef.current) {
        inputRef.current.value = value
      }
    }
    const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
      formatInputWhenValid()
    }

    /**
     * when input validity changes, call the call back if provided
     */
    useEffect(() => {
      onValidChange?.(isValid)
    }, [isValid, onValidChange])

    /**
     * Methods to call for the forwared ref
     */
    useImperativeHandle(forwardedRef, () => ({
      select: () => {
        inputRef.current?.focus()
        inputRef.current?.select()
      },
      getValue: () => inputRef.current?.value ?? '',
    }))

    /**
     * Prevent form submission on enter key
     * When enter key hit, if input is empty, reset its value to initial value, and call onEnter callback if provided
     */
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        if (!inputRef.current?.value) {
          setInputRefValue(String(displayDiscount))
        }
        onEnter?.()
      }
    }
    return (
      <div className={`vd-field vd-mb0 vd-mt0 ${containerCss}`}>
        <div className="vd-value">
          <input
            className={classNames(
              `vd-input wr-sale-discount-input ${inputCssClass}`,
              { 'vd-input--error': !isValid }
            )}
            onChange={onChangeHandler}
            onBlur={onBlurHandler}
            onKeyDown={handleKeyDown}
            ref={inputRef}
          />
          <div
            className={`wr-sale-discount-input-symbol vd-input-icon ${symbolCssClass}`}
          >
            {discountSymbol}
          </div>
        </div>
        {!isValid && (
          <div className="vd-flex vd-text--negative vd-text-supplementary vd-mt3 wr-sale-discount-popover-error-message">
            <Icon
              icon="fa-exclamation-triangle"
              className="wr-sale-discount-error-icon vd-pr1"
            />
            {errorMsg}
          </div>
        )}
      </div>
    )
  }
)
